/* eslint-disable react/jsx-indent, react/jsx-indent-props */
/* eslint-disable react/jsx-closing-bracket-location, react/no-danger */
import React, { useId } from "react";

type HexagonAvatarProps = {
  width?: number | string;
  height?: number | string;
  className?: string;
  imageSrc?: string | null;
  alt?: string;
  /**
   * How the inner image should scale inside the hexagon area.
   * Defaults to 'xMidYMid slice' (cover behavior).
   */
  preserveAspectRatio?: string;
  /**
   * 0-100 percentage to render as progress on the hexagon border.
   * 0 means no progress, 100 means full hexagon.
   */
  progress?: number;
  /** Solid color for the outer progress ring stroke. */
  outerRingColor?: string;
  /** Stroke width for the outer progress ring. */
  outerRingStrokeWidth?: number;

  /** Inner ring stroke color. */
  innerRingColor?: string;
  /** Inner ring stroke width. */
  innerRingStrokeWidth?: number;
  /** Badge logo to display in the bottom-right corner */
  badgeLogo?: string | null;
  /** Initials or fallback text shown when imageSrc is absent */
  label?: string;
};

/**
 * Hexagon-shaped avatar component similar to AchievementAvatar.
 * Renders a hexagon with progress indicator, image, and optional badge.
 */
/**
 * Generate hexagon points centered at (cx, cy) with given radius.
 * Hexagon is oriented point-right (90 degrees).
 */
function generateHexagonPoints(cx: number, cy: number, radius: number): string {
  const points: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i; // Start from right
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    points.push([x, y]);
  }
  return points.map((p) => p.join(",")).join(" ");
}

export function HexagonAchievementAvatar({
  width = 112,
  height = 112,
  className,
  imageSrc,
  alt,
  preserveAspectRatio = "xMidYMid slice",
  progress = 100,
  outerRingColor = "#935B24",
  outerRingStrokeWidth = 3,
  innerRingColor = "transparent",
  innerRingStrokeWidth = 2,
  badgeLogo,
  label,
}: HexagonAvatarProps) {
  const uniqueId = useId().replace(/:/g, "_");
  const hexagonClipId = `hexagon_clip_${uniqueId}`;
  const outerHexagonClipId = `outer_hexagon_clip_${uniqueId}`;
  const cornerClipId = `hexagon_corner_clip_${uniqueId}`;
  const badgeHexagonClipId = `badge_hexagon_clip_${uniqueId}`;

  // Hexagon center and size
  const centerX = 56;
  const centerY = 55.6428;
  const hexagonRadius = 45; // outer radius of the hexagon

  // Progress ring geometry
  const progressRadius = hexagonRadius + outerRingStrokeWidth / 2;
  const hexagonPerimeter = progressRadius * 6; // Perimeter approximation for regular hexagon
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const dashOffset = hexagonPerimeter * (1 - clampedProgress / 100);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={width}
      height={height}
      viewBox="0 0 116 112"
      fill="none"
      className={className}
      role={alt ? "img" : "presentation"}
      aria-label={alt}
    >
      {alt ? <title>{alt}</title> : null}
      <defs>
        {/* Hexagon clip path for the inner image area */}
        <clipPath id={hexagonClipId}>
          <polygon points={generateHexagonPoints(centerX, centerY, hexagonRadius)} />
        </clipPath>

        {/* Outer hexagon for progress ring background */}
        <clipPath id={outerHexagonClipId}>
          <polygon
            points={generateHexagonPoints(centerX, centerY, hexagonRadius + outerRingStrokeWidth)}
          />
        </clipPath>

        {/* ClipPath for the bottom-right corner icon area */}
        <clipPath id={cornerClipId}>
          <rect
            width="26.1841"
            height="34.6667"
            fill="white"
            transform="translate(84.908 76.3096)"
          />
        </clipPath>

        {/* ClipPath for badge logo in hexagonal shape */}
        <clipPath id={badgeHexagonClipId}>
          <path d="M107.634 85.3303V96.6516L97.907 102.308L88.1824 96.6506V85.3293L97.907 79.6741L107.634 85.3303Z" />
        </clipPath>
      </defs>

      {/* Outer hexagon border (progress indicator) */}
      {outerRingColor !== "transparent" && (
        <polygon
          points={generateHexagonPoints(centerX, centerY, progressRadius)}
          stroke={outerRingColor}
          strokeWidth={outerRingStrokeWidth}
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="butt"
          strokeDasharray={hexagonPerimeter}
          strokeDashoffset={dashOffset}
        />
      )}

      {/* Dynamic inner image clipped to hexagon area */}
      {imageSrc ? (
        <image
          href={imageSrc}
          x={centerX - hexagonRadius - 5}
          y={centerY - hexagonRadius - 5}
          width={hexagonRadius * 2 + 10}
          height={hexagonRadius * 2 + 10}
          clipPath={`url(#${hexagonClipId})`}
          preserveAspectRatio={preserveAspectRatio}
        />
      ) : label ? (
        <>
          <polygon
            points={generateHexagonPoints(centerX, centerY, hexagonRadius)}
            fill="#E5E7EB"
            clipPath={`url(#${hexagonClipId})`}
          />
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={28}
            fontWeight="600"
            fill="#6B7280"
            clipPath={`url(#${hexagonClipId})`}
          >
            {label}
          </text>
        </>
      ) : null}

      {/* Inner hexagon ring */}
      {innerRingColor !== "transparent" && (
        <polygon
          points={generateHexagonPoints(centerX, centerY, hexagonRadius - innerRingStrokeWidth)}
          stroke={innerRingColor}
          strokeWidth={innerRingStrokeWidth}
          fill="none"
          strokeLinejoin="round"
        />
      )}

      {/* Bottom-right corner badge logo - only show if badgeLogo is provided */}
      {badgeLogo && (
        <g clipPath={`url(#${cornerClipId})`}>
          <g>
            {/* White border for badge logo */}
            <path
              d="M107.634 85.3303V96.6516L97.907 102.308L88.1824 96.6506V85.3293L97.907 79.6741L107.634 85.3303Z"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
            {/* Badge logo image */}
            <g clipPath={`url(#${badgeHexagonClipId})`}>
              <image
                href={badgeLogo}
                x={86.5}
                y={78}
                width={23}
                height={27}
                preserveAspectRatio="xMidYMid slice"
              />
            </g>
          </g>
        </g>
      )}
    </svg>
  );
}

export default HexagonAchievementAvatar;
